import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Paper,
  Avatar,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import EditIcon from "@mui/icons-material/Edit";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BadgeIcon from "@mui/icons-material/Badge";
import SearchOffIcon from "@mui/icons-material/SearchOff";

import { styled, alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { Fade } from "@mui/material";

import { useNavigate } from "react-router-dom";
import axios from "axios";

import API_BASE_URL, { BASE_URL } from "../config";
import UserProfileDialog from "./UserProfileDialog";
import { getErrorMessage } from "../utils/apiError";

// ======================================================
// Styled Components
// ======================================================

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(
    135deg,
    ${theme.palette.background.paper} 0%,
    ${alpha(theme.palette.primary.light, 0.1)} 100%
  )`,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4),
  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(2),
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
  },
}));

// ======================================================
// Image URL Helper
// ======================================================

const getImageUrl = (path) => {
  if (!path) {
    return "/chaudharylife/uploads/default-profile.png";
  }

  if (path.startsWith("blob:")) {
    return path;
  }

  if (path.startsWith("http") && !path.includes("localhost")) {
    return path;
  }

  if (path.startsWith("http") && path.includes("localhost")) {
    return path.replace(/http:\/\/localhost:\d+/, BASE_URL);
  }

  if (path.startsWith("/")) {
    return `${BASE_URL}${path}`;
  }

  return `${BASE_URL}/uploads/${path}`;
};

// ======================================================
// Search Component
// ======================================================

export default function Search() {
  const theme = useTheme();
  const navigate = useNavigate();

  // ====================================================
  // Search Fields
  // ====================================================

  const [first_name, setFirstName] = useState("");
  const [area, setArea] = useState("");
  const [district, setDistrict] = useState("");
  const [state, setState] = useState("");
  const [uid, setUid] = useState("");

  // ====================================================
  // Search Result
  // ====================================================

  const [searchResults, setSearchResults] = useState([]);

  const [selectedUser, setSelectedUser] = useState(null);

  const [openProfileDialog, setOpenProfileDialog] = useState(false);

  const [loading, setLoading] = useState(false);

  // ====================================================
  // Comments
  // ====================================================

  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);

  const [selectedUserComments, setSelectedUserComments] = useState([]);

  const [currentUserComment, setCurrentUserComment] = useState("");

  const [isEditingComment, setIsEditingComment] = useState(false);

  const [currentCommentUserId, setCurrentCommentUserId] = useState(null);

  // ====================================================
  // Snackbar
  // ====================================================

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ====================================================
  // Search Validation
  // ====================================================

  const isSearchDisabled = !first_name && !area && !district && !state && !uid;

  // ====================================================
  // Search Users
  // ====================================================

  const handleSearch = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/search`, {
        params: {
          first_name,
          area,
          district,
          state,
          uid: uid.trim(),
        },

        withCredentials: true,
      });

      setSearchResults(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      } else {
        setSnackbar({
          open: true,
          message: getErrorMessage(error),
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // ====================================================
  // Open User Profile
  // ====================================================

  const fetchUserProfile = async (userUid) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/users/${userUid}`,
        {
          withCredentials: true,
        },
      );

      const data = response.data;

      setSelectedUser(data);
      setOpenProfileDialog(true);

      // Track profile visit
      const currentUserUid = localStorage.getItem("uid");

      if (currentUserUid && currentUserUid !== userUid) {
        axios
          .post(
            `${API_BASE_URL}/visitors/track`,
            {
              visited_uid: userUid,
              visitor_uid: currentUserUid,
            },
            {
              withCredentials: true,
            },
          )
          .catch(() => {});
      }
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.isPrivate) {
        setSnackbar({
          open: true,
          message: "This profile is private.",
          severity: "info",
        });
      } else if (error.response?.status === 401) {
        navigate("/login");
      } else {
        setSnackbar({
          open: true,
          message: getErrorMessage(error),
          severity: "error",
        });
      }
    }
  };

  // ====================================================
  // Open Comments
  // ====================================================

  const handleOpenComments = async (userUid) => {
    setLoading(true);

    setCurrentCommentUserId(userUid);

    try {
      const response = await axios.get(`${API_BASE_URL}/comments/${userUid}`, {
        withCredentials: true,
      });

      const comments = Array.isArray(response.data) ? response.data : [];

      setSelectedUserComments(comments);

      const currentUserUid = localStorage.getItem("uid");

      const userComment = comments.find(
        (comment) => comment.commenter_uid === currentUserUid,
      );

      if (userComment) {
        setCurrentUserComment(userComment.comment || "");

        setIsEditingComment(true);
      } else {
        setCurrentUserComment("");
        setIsEditingComment(false);
      }

      setCommentsDialogOpen(true);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      } else {
        setSnackbar({
          open: true,
          message: getErrorMessage(error),
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // ====================================================
  // Add / Edit Comment
  // ====================================================

  const handleSubmitComment = async () => {
    if (!currentCommentUserId) {
      return;
    }

    const currentUserUid = localStorage.getItem("uid");

    if (!currentUserUid) {
      navigate("/login");
      return;
    }

    try {
      const method = isEditingComment ? "put" : "post";

      const endpoint = isEditingComment
        ? `${API_BASE_URL}/comments/${currentCommentUserId}/edit`
        : `${API_BASE_URL}/comments/${currentCommentUserId}`;

      await axios({
        method,
        url: endpoint,

        data: {
          comment: currentUserComment,
          commenter_uid: currentUserUid,
        },

        withCredentials: true,
      });

      // Fetch latest comments
      const response = await axios.get(
        `${API_BASE_URL}/comments/${currentCommentUserId}`,
        {
          withCredentials: true,
        },
      );

      setSelectedUserComments(
        Array.isArray(response.data) ? response.data : [],
      );

      setSnackbar({
        open: true,
        message: `Comment ${isEditingComment ? "updated" : "added"}!`,
        severity: "success",
      });

      setIsEditingComment(true);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      } else {
        setSnackbar({
          open: true,
          message: getErrorMessage(error),
          severity: "error",
        });
      }
    }
  };

  // ====================================================
  // UI
  // ====================================================

  return (
    <div>
      <Fade in timeout={1000}>
        <StyledPaper>
          {/* ==========================================
              HEADER
          ========================================== */}

          <Box
            sx={{
              textAlign: "center",
              mb: 4,
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 700,

                background: `linear-gradient(
                  45deg,
                  ${theme.palette.primary.main},
                  ${theme.palette.secondary.main}
                )`,

                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Find People
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Search by name, city, district, state, or unique ID.
            </Typography>
          </Box>

          {/* ==========================================
              SEARCH FIELDS
          ========================================== */}

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },

              gap: 2,
              mb: 3,
            }}
          >
            <StyledTextField
              fullWidth
              label="Name"
              value={first_name}
              onChange={(e) => setFirstName(e.target.value)}
            />

            <StyledTextField
              fullWidth
              label="City/Town/Village"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            />

            <StyledTextField
              fullWidth
              label="District"
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
            />

            <StyledTextField
              fullWidth
              label="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
            />

            <StyledTextField
              fullWidth
              label="Unique ID"
              value={uid}
              onChange={(e) => setUid(e.target.value)}
            />
          </Box>

          {/* ==========================================
              SEARCH BUTTON
          ========================================== */}

          <Box
            sx={{
              textAlign: "center",
              mb: 4,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isSearchDisabled || loading}
              startIcon={
                loading ? <CircularProgress size={18} color="inherit" /> : null
              }
              sx={{
                borderRadius: 2,
                px: 4,
              }}
            >
              {loading ? "Searching..." : "Search"}
            </Button>
          </Box>

          {/* ==========================================
              RESULTS
          ========================================== */}

          <Box>
            {searchResults.length > 0 ? (
              <Grid container spacing={3}>
                {searchResults.map((result, index) => (
                  <Grid item xs={12} key={result.uid || result.id || index}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 2,

                        background: alpha(theme.palette.background.paper, 0.9),

                        boxShadow: `0 4px 20px 0 ${alpha(
                          theme.palette.common.black,
                          0.05,
                        )}`,

                        position: "relative",
                        overflow: "hidden",

                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: "3px",

                          background: `linear-gradient(
                              90deg,
                              ${theme.palette.primary.main},
                              ${theme.palette.secondary.main}
                            )`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 3,
                        }}
                      >
                        <Avatar
                          src={getImageUrl(result.image_url)}
                          sx={{
                            width: 80,
                            height: 80,
                            fontSize: "2rem",

                            background: `linear-gradient(
                                45deg,
                                ${theme.palette.primary.main},
                                ${theme.palette.secondary.main}
                              )`,
                          }}
                        >
                          {result.first_name?.[0]}
                        </Avatar>

                        <Box
                          sx={{
                            flex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 1,
                              gap: 2,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,

                                background: `linear-gradient(
                                    45deg,
                                    ${theme.palette.primary.main},
                                    ${theme.palette.secondary.main}
                                  )`,

                                backgroundClip: "text",

                                WebkitBackgroundClip: "text",

                                color: "transparent",
                              }}
                            >
                              {result.first_name} {result.last_name}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => fetchUserProfile(result.uid)}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: "none",
                                }}
                              >
                                View Profile
                              </Button>

                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CommentIcon />}
                                onClick={() => handleOpenComments(result.uid)}
                                sx={{
                                  borderRadius: 2,

                                  textTransform: "none",

                                  borderColor: alpha(
                                    theme.palette.secondary.main,
                                    0.5,
                                  ),

                                  color: "secondary.main",
                                }}
                              >
                                Comments
                              </Button>
                            </Box>
                          </Box>

                          {/* Location */}

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mb: 0.5,
                            }}
                          >
                            <LocationOnIcon
                              sx={{
                                fontSize: 16,
                              }}
                            />

                            {[result.area, result.district, result.state]
                              .filter(Boolean)
                              .join(", ") || "Location not specified"}
                          </Typography>

                          {/* UID */}

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <BadgeIcon
                              sx={{
                                fontSize: 16,
                              }}
                            />
                            UID: {result.uid}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                }}
              >
                <SearchOffIcon
                  sx={{
                    fontSize: 48,
                    color: "text.disabled",
                    mb: 1,
                  }}
                />

                <Typography variant="body1" color="text.secondary">
                  No results found. Try different search terms.
                </Typography>
              </Box>
            )}
          </Box>

          {/* ==========================================
              PROFILE DIALOG
          ========================================== */}

          <UserProfileDialog
            selectedUser={selectedUser}
            openProfileDialog={openProfileDialog}
            handleProfileClose={() => {
              setOpenProfileDialog(false);
              setSelectedUser(null);
            }}
          />

          {/* ==========================================
              COMMENTS DIALOG
          ========================================== */}

          <Dialog
            open={commentsDialogOpen}
            onClose={() => setCommentsDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {isEditingComment ? "Edit Comment" : "Comments"}

              <IconButton onClick={() => setCommentsDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Write your comment..."
                  value={currentUserComment}
                  onChange={(e) => setCurrentUserComment(e.target.value)}
                  sx={{ mb: 1 }}
                />

                <Button
                  variant="contained"
                  onClick={handleSubmitComment}
                  disabled={!currentUserComment.trim()}
                >
                  {isEditingComment ? "Update" : "Add Comment"}
                </Button>
              </Box>

              {selectedUserComments.length === 0 ? (
                <Typography color="text.secondary" align="center">
                  Be the first to comment!
                </Typography>
              ) : (
                selectedUserComments.map((comment) => {
                  const isOwn =
                    comment.commenter_uid === localStorage.getItem("uid");

                  return (
                    <Paper
                      key={comment.id}
                      sx={{
                        p: 2,
                        mb: 1,

                        border: isOwn
                          ? `1px solid ${theme.palette.primary.light}`
                          : "none",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                          gap: 2,
                        }}
                      >
                        <Typography variant="subtitle2" color="primary">
                          {comment.commenter_name}

                          {isOwn && (
                            <span
                              style={{
                                color: "#999",
                                fontSize: 12,
                                marginLeft: 6,
                              }}
                            >
                              (You)
                            </span>
                          )}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {comment.createdAt
                              ? new Date(comment.createdAt).toLocaleDateString()
                              : ""}
                          </Typography>

                          {isOwn && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setCurrentUserComment(comment.comment || "");

                                setIsEditingComment(true);

                                setCurrentCommentUserId(comment.user_uid);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      <Typography variant="body2">{comment.comment}</Typography>
                    </Paper>
                  );
                })
              )}
            </DialogContent>
          </Dialog>

          {/* ==========================================
              SNACKBAR
          ========================================== */}

          <Snackbar
            open={snackbar.open}
            autoHideDuration={5002}
            onClose={() =>
              setSnackbar((prev) => ({
                ...prev,
                open: false,
              }))
            }
          >
            <Alert
              severity={snackbar.severity}
              onClose={() =>
                setSnackbar((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
              sx={{
                width: "100%",
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </StyledPaper>
      </Fade>
    </div>
  );
}
